<template>
    <div class="space-y-5">
        <h2 class="text-xl font-semibold text-center outline-none">{{ $t('views.feed.reconciliations.newModal.title') }}</h2>

        <!-- Controls row -->
        <div class="flex items-center gap-3">
            <div class="relative flex-shrink-0 bg-gray-100 rounded overflow-hidden pt-6 pb-1 px-5 cursor-default whitespace-nowrap">
                <span class="absolute top-1.5 left-5 text-gray-400" style="font-size: 0.8125rem">
                    {{ $t('views.feed.reconciliations.newModal.registrationSummary.period') }}
                </span>
                <span class="text-sm text-dark-gray-600">{{ formattedFromDate }} – {{ formattedToDate }}</span>
            </div>

            <!-- Typeahead: add feed mix -->
            <div class="relative flex-1">
                <input
                    v-model="addSearch"
                    type="text"
                    :placeholder="$t('views.feed.reconciliations.newModal.registrationSummary.addFeedmixPlaceholder')"
                    class="w-full px-4 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:border-primary-800"
                    @focus="showAddDropdown = true"
                    @blur="onAddBlur"
                />
                <div
                    v-if="showAddDropdown && filteredAvailableMixes.length"
                    class="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
                >
                    <button
                        v-for="mix in filteredAvailableMixes"
                        :key="mix.id"
                        type="button"
                        class="w-full text-left px-4 py-2 text-[0.9rem] hover:bg-gray-50"
                        @mousedown.prevent="addFeedMix(mix)"
                    >
                        {{ mix.name }}
                    </button>
                </div>
            </div>

        </div>

        <!-- Loading / Table -->
        <div v-if="loading" class="flex justify-center py-8">
            <Spinner class="w-8 h-8" />
        </div>
        <div v-else-if="summary">
            <!-- Column headers -->
            <div class="grid grid-cols-[2fr_1.5fr_1fr_1fr_2rem_2rem] gap-x-3 px-6 pb-2 text-[0.9rem] text-gray-500">
                <span>{{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.name') }}</span>
                <span>{{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.type') }}</span>
                <span class="text-right text-primary-800 leading-tight">
                    {{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.registeredConsumptionShort') }}, kg
                </span>
                <span class="text-right text-primary-800 leading-tight">
                    {{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.actualConsumptionShort') }}, kg
                </span>
                <span></span>
                <span></span>
            </div>

            <!-- Feed mix rows -->
            <div>
                <div v-for="item in summary" :key="item.feedmix.id" class="rounded odd:bg-dark-gray-200">
                    <!-- Feed mix row -->
                    <div class="grid grid-cols-[2fr_1.5fr_1fr_1fr_2rem_2rem] items-center gap-x-3 px-6 py-4">
                        <span class="text-[0.9rem] font-medium text-primary-800">{{ item.feedmix.name }}</span>
                        <span class="text-[0.9rem] text-gray-500">{{ item.feed_type.name }}</span>
                        <span class="text-right text-[0.9rem] text-gray-400">{{ formatNumber(item.consumption_over_period) }}</span>
                        <div class="flex justify-end">
                            <SimpleInput
                                        grouped
                                v-if="canEditFeedMix(item)"
                                :value="rowStates[item.feedmix.id].actualConsumption ?? ''"
                                type="number"
                                background-color="bg-white"
                                class="w-24 text-right text-[0.9rem] font-medium text-primary-800 border border-gray-300 rounded"
                                @keydown="onNumberKeydown"
                                @update:value="rowStates[item.feedmix.id].actualConsumption = ($event === '' || $event === null ? null : toPositive($event))"
                            />
                            <span v-else class="text-[0.9rem] text-gray-400">{{ formatNumber(getFeedMixDisplayActual(item)) }}</span>
                        </div>
                        <button
                            type="button"
                            class="flex items-center justify-center"
                            @click="rowStates[item.feedmix.id].expanded = !rowStates[item.feedmix.id].expanded"
                        >
                            <Icon
                                :src="IconSource.DropdownDark"
                                class="w-5 h-5 transition-transform duration-200"
                                :class="{ 'rotate-180': rowStates[item.feedmix.id].expanded }"
                            />
                        </button>
                        <button
                            v-if="rowStates[item.feedmix.id]?.isAdded"
                            type="button"
                            class="flex items-center justify-center text-gray-400 hover:text-red-500"
                            @click="removeRow(item)"
                        >
                            <Icon :src="IconSource.Close" class="w-5 h-5" />
                        </button>
                        <button v-else type="button" class="flex items-center justify-center text-gray-400 hover:text-gray-600" @click="resetRow(item)">
                            <Icon :src="IconSource.RefreshActivities" class="w-5 h-5" />
                        </button>
                    </div>

                    <!-- Expanded sub-section -->
                    <div v-show="rowStates[item.feedmix.id] && rowStates[item.feedmix.id].expanded" class="border-t border-gray-200 border-l-4 border-primary-300">

                        <!-- Compound Feed (Færdigfoder): inventory flow — start + delivered − remaining = consumption (ADR-0006) -->
                        <div v-if="isCompoundFeed(item)" class="bg-gray-50">
                            <!-- Column headers -->
                            <div class="grid grid-cols-[1fr_1fr_1fr_1fr_1fr] gap-x-3 px-6 py-2 border-b border-gray-200 text-xs text-gray-500">
                                <span>{{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.initialAmount') }}, kg</span>
                                <span class="text-right">{{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.deliveredAmount') }}, kg</span>
                                <span class="text-right">{{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.availableAmount') }}, kg</span>
                                <span class="text-right text-primary-800">{{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.remainderAmount') }}, kg</span>
                                <span class="text-right">{{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.actualAmount') }}, kg</span>
                            </div>
                            <!-- Data row -->
                            <div class="grid grid-cols-[1fr_1fr_1fr_1fr_1fr] items-center gap-x-3 px-6 py-3">
                                <span class="text-[0.9rem] text-gray-500">{{ formatNumber(item.initial_amount_from_preceding_period ?? 0) }}</span>
                                <span class="text-right text-[0.9rem] text-gray-500">{{ formatNumber(item.delivered_in_period ?? 0) }}</span>
                                <span class="text-right text-[0.9rem] text-gray-500">{{ formatNumber(getCompoundAvailable(item)) }}</span>
                                <div class="flex justify-end">
                                    <SimpleInput
                                        grouped
                                        :value="rowStates[item.feedmix.id].remainder ?? ''"
                                        type="number"
                                        background-color="bg-white"
                                        class="w-24 text-right text-[0.9rem] font-medium text-primary-800 border border-gray-300 rounded"
                                        @keydown="onNumberKeydown"
                                        @update:value="onRemainderInput(item, $event)"
                                    />
                                </div>
                                <span class="text-right text-[0.9rem] font-medium text-primary-800">
                                    {{ formatNumber(getCompoundConsumption(item)) }}
                                </span>
                            </div>
                        </div>

                        <!-- Type 1 (HOME_MIX) only: mode toggle + raw material rows -->
                        <template v-else-if="isType1Multi(item)">
                            <!-- Mode toggle bar (only for type 1 multi with components) -->
                            <div v-if="hasComponents(item)" class="flex items-center gap-2 px-6 py-2 bg-gray-50 border-b border-gray-200 text-xs">
                                <span class="text-gray-500 mr-1">{{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.editLabel') }}</span>
                                <button
                                    type="button"
                                    class="px-2 py-0.5 rounded text-xs font-medium border"
                                    :class="rowStates[item.feedmix.id].editMode === 'aggregate' ? 'bg-primary-800 text-white border-primary-800' : 'bg-white text-gray-600 border-gray-300'"
                                    @click="setEditMode(item.feedmix.id, 'aggregate')"
                                >
                                    {{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.editAggregate') }}
                                </button>
                                <button
                                    type="button"
                                    class="px-2 py-0.5 rounded text-xs font-medium border"
                                    :class="rowStates[item.feedmix.id].editMode === 'components' ? 'bg-primary-800 text-white border-primary-800' : 'bg-white text-gray-600 border-gray-300'"
                                    @click="setEditMode(item.feedmix.id, 'components')"
                                >
                                    {{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.editComponents') }}
                                </button>
                            </div>
                            <!-- Inline warning when components mode was overridden to aggregate -->
                            <div v-if="rowStates[item.feedmix.id]?.componentsModeOverridden" class="flex items-center gap-1 px-6 py-2 bg-gray-50 border-b border-gray-200 text-amber-600 text-xs">
                                <Icon :src="IconSource.Warning" class="w-4 h-4 flex-shrink-0" />
                                {{ $t('views.feed.reconciliations.newModal.registrationSummary.componentsModeOverriddenWarning') }}
                            </div>

                            <!-- Raw material column headers -->
                            <div class="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_2rem_2rem] gap-x-3 px-6 py-1.5 text-xs text-gray-500 bg-gray-50 border-b border-gray-200">
                                <span></span>
                                <span class="text-right">{{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.registeredConsumptionShort') }}</span>
                                <span class="text-right text-primary-800">{{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.actualConsumptionShort') }}</span>
                                <span class="text-right">{{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.registeredPriceShort') }}{{ priceUnitSuffix }}</span>
                                <span class="text-right text-primary-800">{{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.actualPriceShort') }}{{ priceUnitSuffix }}</span>
                                <span></span>
                                <span></span>
                            </div>

                            <!-- Raw material rows -->
                            <div
                                v-for="mat in getEffectiveRawMaterials(item)"
                                :key="mat.raw_material_id"
                                class="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_2rem_2rem] items-center gap-x-3 px-6 py-3 bg-gray-50 border-b last:border-b-0 border-gray-100"
                            >
                                <span class="pl-6 text-sm text-gray-600">{{ mat.name }}</span>
                                <span class="text-right text-sm text-gray-400">{{ formatNumber(mat.consumption_over_period) }}</span>
                                <div class="flex justify-end">
                                    <SimpleInput
                                        grouped
                                        v-if="canEditRawMat(item)"
                                        :value="rowStates[item.feedmix.id].rawActuals[mat.raw_material_id] ?? ''"
                                        type="number"
                                        background-color="bg-white"
                                        class="w-24 text-right text-sm border border-gray-300 rounded"
                                        @keydown="onNumberKeydown"
                                        @update:value="rowStates[item.feedmix.id].rawActuals[mat.raw_material_id] = ($event === '' || $event === null ? null : toPositive($event))"
                                    />
                                    <span v-else class="text-sm text-gray-400">{{ formatNumber(rowStates[item.feedmix.id].rawActuals[mat.raw_material_id] ?? mat.consumption_over_period) }}</span>
                                </div>
                                <span class="text-right text-sm text-gray-400">{{ formatNumber(mat.price_per_100kg) }}</span>
                                <div class="flex justify-end">
                                    <SimpleInput
                                        grouped
                                        v-if="canEditRawMat(item)"
                                        :value="rowStates[item.feedmix.id].rawPrices[mat.raw_material_id] ?? ''"
                                        type="number"
                                        background-color="bg-white"
                                        class="w-24 text-right text-sm border border-gray-300 rounded"
                                        @keydown="onNumberKeydown"
                                        @update:value="rowStates[item.feedmix.id].rawPrices[mat.raw_material_id] = ($event === '' || $event === null ? null : toPositive($event))"
                                    />
                                    <span v-else class="text-sm text-gray-400">–</span>
                                </div>
                                <span></span>
                                <span></span>
                            </div>
                        </template>

                        <!-- Price & energy content (always shown) -->
                        <div class="bg-gray-50 border-t border-gray-200">
                            <div class="grid grid-cols-[1fr_1fr_1fr_1fr] gap-x-3 px-6 pt-2 pb-1 text-xs text-gray-500">
                                <span>{{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.registeredPriceShort') }}{{ priceUnitSuffix }}</span>
                                <span class="text-primary-800">{{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.actualPriceShort') }}{{ priceUnitSuffix }}</span>
                                <span>{{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.registeredEnergyContentShort') }}{{ energyUnitSuffix }}</span>
                                <span class="text-primary-800">{{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.actualEnergyContentShort') }}{{ energyUnitSuffix }}</span>
                            </div>
                            <div class="grid grid-cols-[1fr_1fr_1fr_1fr] gap-x-3 items-center px-6 pb-3">
                                <span class="text-[0.9rem] text-gray-500">{{ formatNumber(item.price_per_100kg) }}</span>
                                <SimpleInput
                                        grouped
                                    :value="rowStates[item.feedmix.id].actualPrice ?? ''"
                                    type="number"
                                    background-color="bg-white"
                                    class="w-24 text-right text-[0.9rem] font-medium text-primary-800 border border-gray-300 rounded"
                                    @keydown="onNumberKeydown"
                                    @update:value="rowStates[item.feedmix.id].actualPrice = ($event === '' || $event === null ? null : toPositive($event))"
                                />
                                <span class="text-[0.9rem] text-gray-500">{{ item.energy_per_100kg !== null && item.energy_per_100kg !== undefined ? formatNumber(item.energy_per_100kg) : '–' }}</span>
                                <SimpleInput
                                        grouped
                                    :value="rowStates[item.feedmix.id].actualEnergyContent ?? ''"
                                    type="number"
                                    background-color="bg-white"
                                    class="w-24 text-right text-[0.9rem] font-medium text-primary-800 border border-gray-300 rounded"
                                    @keydown="onNumberKeydown"
                                    @update:value="rowStates[item.feedmix.id].actualEnergyContent = ($event === '' || $event === '-' || $event === '.' || $event === ',') ? null : toPositive($event)"
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Total row: sum of registered and actual consumption across all feed mixes -->
                <div v-if="summary.length" class="grid grid-cols-[2fr_1.5fr_1fr_1fr_2rem_2rem] items-center gap-x-3 px-6 py-4 border-t-2 border-gray-300">
                    <span class="text-[0.9rem] font-semibold text-primary-800">{{ $t('views.feed.reconciliations.newModal.registrationSummary.columns.total') }}</span>
                    <span></span>
                    <span class="text-right text-[0.9rem] font-semibold text-gray-500">{{ formatNumber(totalRegisteredConsumption) }}</span>
                    <span class="text-right text-[0.9rem] font-semibold text-primary-800">{{ formatNumber(totalActualConsumption) }}</span>
                    <span></span>
                    <span></span>
                </div>
            </div>
        </div>

        <!-- Footer -->
        <p v-if="errorMessage" class="text-red-500 text-sm text-right">{{ errorMessage }}</p>
        <div class="flex items-center space-x-4">
            <DeleteReconciliationConfirmationModal
                v-if="reconciliation !== null && reconciliation !== undefined"
                :reconciliation="reconciliation"
                @deleted="$emit('deleted')"
            >
                <template #default="scope">
                    <DeleteButton @click="scope.open">
                        {{ $t('views.feed.reconciliations.editModal.deleteButton') }}
                    </DeleteButton>
                </template>
            </DeleteReconciliationConfirmationModal>
            <CustomButton class="min-w-44 ml-auto" :disabled="!summary || loading || isSubmitting || actualsLoading" @click="onSubmitClicked">
                {{ reconciliation !== null && reconciliation !== undefined
                    ? $t('views.feed.reconciliations.newModal.updateButton')
                    : $t('views.feed.reconciliations.newModal.createButton') }}
            </CustomButton>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useQueryClient } from '@tanstack/vue-query';
import { Form } from '@makeabledk/vue-ui/support/http';
import CustomButton from '@/components/ui/CustomButton.vue';
import DeleteButton from '@/components/ui/DeleteButton.vue';
import Spinner from '@/components/ui/Spinner.vue';
import SimpleInput from '@/components/ui/SimpleInput.vue';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { FeedReconciliation, FeedReconciliationDetail, FeedReconciliationRegSummary, FeedReconciliationRegSummaryRawMaterial, FeedReconciliationRegSummaryResponse, IconSource, Mix } from '@/types';
import { MixTypeId } from '@/types/MixType';
import { i18n } from '@/plugins/internationalization/i18n';
import DeleteReconciliationConfirmationModal from '@/views/dashboard/feed/feedReconciliations/editReconciliationModal/DeleteReconciliationConfirmationModal.vue';
import { queryKeys } from '@/plugins/store/actions/queries/feedReconciliations';

type FeedMixRowState = {
    expanded: boolean;
    editMode: 'aggregate' | 'components';
    componentsModeOverridden: boolean;
    actualConsumption: number | null;
    // Compound Feed (Færdigfoder) only: the farmer enters the remaining amount left in
    // storage; consumption is derived as starting + delivered − remaining (see ADR-0006).
    remainder: number | null;
    rawActuals: Record<number, number | null>;
    actualPrice: number | null;
    rawPrices: Record<number, number | null>;
    actualEnergyContent: number | null;
    isAdded: boolean;
};

export default defineComponent({
    components: { CustomButton, DeleteButton, Spinner, SimpleInput, DeleteReconciliationConfirmationModal },
    props: {
        value: {
            type: Object as () => Form<any> & any,
            required: true,
        },
        reconciliationId: {
            type: Number,
            default: null,
        },
        reconciliation: {
            type: Object as () => FeedReconciliation,
            default: null,
        },
    },
    emits: ['update:value', 'submit', 'close', 'deleted'],
    setup(props, { emit }) {
        const queryClient = useQueryClient();
        const loading = ref(true);
        const isSubmitting = ref(false);
        const errorMessage = ref<string | null>(null);
        const actualsLoading = ref(false);
        const hasAppliedActuals = ref(false);
        const summary = ref<FeedReconciliationRegSummary[] | null>(null);
        const addSearch = ref('');
        const showAddDropdown = ref(false);
        const rowStates = ref<Record<number, FeedMixRowState>>({});

        const availableMixes = computed(() => {
            const inTable = new Set((summary.value ?? []).map((s) => s.feedmix.id));
            return (store.state.mixes as Mix[]).filter((m) => m.is_active && !inTable.has(m.id));
        });

        const filteredAvailableMixes = computed(() => {
            const q = addSearch.value.trim().toLowerCase();
            if (!q) return availableMixes.value;
            return availableMixes.value.filter((m) => m.name.toLowerCase().includes(q));
        });

        function formatNumber(value: number | null | undefined): string {
            if (value === null || value === undefined || !isFinite(value)) return '–';
            return i18n.global.n(value, 'decimal');
        }

        function toPositive(value: number | string): number {
            const n = Number(value);
            return isFinite(n) ? Math.max(0, n) : 0;
        }

        function onNumberKeydown(e: KeyboardEvent) {
            if (e.ctrlKey || e.metaKey) return;
            if (e.key.length > 1) return;
            if (!/[0-9.,]/.test(e.key)) e.preventDefault();
        }

        const formattedFromDate = computed(() => {
            const d = props.value?.fromStatusDate;
            return d ? i18n.global.d(new Date(d), 'short') : '';
        });

        const formattedToDate = computed(() => {
            const d = props.value?.toStatusDate;
            return d ? i18n.global.d(new Date(d), 'short') : '';
        });

        const priceUnitSuffix = computed(() => {
            const unit = store.getters.priceCurrencyPerWeightUnitDisplayText;
            return unit ? ` (${unit})` : '';
        });

        const energyUnitSuffix = computed(() => {
            const unit = store.getters.energyPerWeightUnitDisplayText;
            return unit ? ` (${unit})` : '';
        });

function isType1Multi(item: FeedReconciliationRegSummary): boolean {
            return item.feed_type.id === MixTypeId.HOME_MIX;
        }

        function isCompoundFeed(item: FeedReconciliationRegSummary): boolean {
            return item.feed_type.id === MixTypeId.COMPOUND_MIX;
        }

        // Available stock for Compound Feed = starting amount (previous period's remainder) + delivered in period.
        function getCompoundAvailable(item: FeedReconciliationRegSummary): number {
            return (item.initial_amount_from_preceding_period ?? 0) + (item.delivered_in_period ?? 0);
        }

        // Derived consumption for Compound Feed = available − remaining, never negative (remaining is clamped to available).
        function getCompoundConsumption(item: FeedReconciliationRegSummary): number {
            const remainder = rowStates.value[item.feedmix.id]?.remainder ?? 0;
            return Math.max(0, getCompoundAvailable(item) - remainder);
        }

        // Clamp the entered remaining amount to [0, available] so derived consumption stays non-negative.
        function onRemainderInput(item: FeedReconciliationRegSummary, value: string | number | null) {
            const state = rowStates.value[item.feedmix.id];
            if (!state) return;
            if (value === '' || value === null) {
                state.remainder = null;
                return;
            }
            state.remainder = Math.min(toPositive(value), getCompoundAvailable(item));
        }

        function getMixById(feedmixId: number): Mix | undefined {
            return (store.state.mixes as Mix[])?.find((m: Mix) => m.id === feedmixId);
        }

        function hasComponents(item: FeedReconciliationRegSummary): boolean {
            return isType1Multi(item) && (getMixById(item.feedmix.id)?.components?.length ?? 0) > 0;
        }

        function getEffectiveRawMaterials(item: FeedReconciliationRegSummary): FeedReconciliationRegSummaryRawMaterial[] {
            const mix = getMixById(item.feedmix.id);
            if (mix?.components?.length) {
                return mix.components
                    .filter((c) => c.material.id != null)
                    .map((c) => {
                        const summaryMat = item.raw_materials.find((m) => m.raw_material_id === c.material.id);
                        return summaryMat ?? {
                            raw_material_id: c.material.id!,
                            name: c.material.name,
                            type_name: '',
                            consumption_over_period: null,
                            price_per_100kg: null,
                        };
                    });
            }
            return item.raw_materials;
        }

        function canEditFeedMix(item: FeedReconciliationRegSummary): boolean {
            // Compound Feed consumption is derived from the remaining amount, so the main-row cell is read-only.
            if (isCompoundFeed(item)) return false;
            return !isType1Multi(item) || rowStates.value[item.feedmix.id]?.editMode === 'aggregate';
        }

        function canEditRawMat(item: FeedReconciliationRegSummary): boolean {
            return isType1Multi(item) && rowStates.value[item.feedmix.id]?.editMode === 'components';
        }

        function getFeedMixDisplayActual(item: FeedReconciliationRegSummary): number | null {
            const state = rowStates.value[item.feedmix.id];
            if (!state) return item.consumption_over_period ?? 0;
            if (isCompoundFeed(item)) {
                return getCompoundConsumption(item);
            }
            if (isType1Multi(item) && state.editMode === 'components') {
                return getEffectiveRawMaterials(item).reduce((s, m) => s + (state.rawActuals[m.raw_material_id] ?? m.consumption_over_period ?? 0), 0);
            }
            return state.actualConsumption;
        }

        const totalRegisteredConsumption = computed(() =>
            (summary.value ?? []).reduce((sum, item) => sum + (item.consumption_over_period ?? 0), 0)
        );

        const totalActualConsumption = computed(() =>
            (summary.value ?? []).reduce((sum, item) => sum + (getFeedMixDisplayActual(item) ?? 0), 0)
        );

        function setEditMode(feedmixId: number, mode: 'aggregate' | 'components') {
            const state = rowStates.value[feedmixId];
            if (!state) return;
            if (mode === 'aggregate' && summary.value) {
                const item = summary.value.find((i) => i.feedmix.id === feedmixId);
                if (item && !state.actualConsumption) {
                    state.actualConsumption = getEffectiveRawMaterials(item).reduce(
                        (s, m) => s + (state.rawActuals[m.raw_material_id] ?? m.consumption_over_period ?? 0),
                        0
                    );
                }
            }
            state.editMode = mode;
        }

        function resetRow(item: FeedReconciliationRegSummary) {
            const state = rowStates.value[item.feedmix.id];
            if (!state) return;
            state.actualConsumption = item.consumption_over_period ?? 0;
            state.remainder = 0;
            state.actualPrice = item.price_per_100kg ?? null;
            state.actualEnergyContent = item.energy_per_100kg ?? null;
            for (const m of item.raw_materials) {
                state.rawActuals[m.raw_material_id] = m.consumption_over_period ?? 0;
                state.rawPrices[m.raw_material_id] = m.price_per_100kg ?? null;
            }
            state.editMode = 'aggregate';
            state.expanded = false;
        }

        function removeRow(item: FeedReconciliationRegSummary) {
            if (!summary.value) return;
            summary.value = summary.value.filter((s) => s.feedmix.id !== item.feedmix.id);
            delete rowStates.value[item.feedmix.id];
        }

        function initRowState(item: FeedReconciliationRegSummary, isAdded = false): FeedMixRowState {
            const rawActuals: Record<number, number | null> = {};
            const rawPrices: Record<number, number | null> = {};
            for (const m of item.raw_materials) {
                rawActuals[m.raw_material_id] = m.consumption_over_period ?? 0;
                rawPrices[m.raw_material_id] = m.price_per_100kg ?? null;
            }
            return {
                expanded: false,
                editMode: 'aggregate',
                componentsModeOverridden: false,
                actualConsumption: item.consumption_over_period ?? 0,
                remainder: 0,
                rawActuals,
                actualPrice: item.price_per_100kg ?? null,
                rawPrices,
                actualEnergyContent: item.energy_per_100kg ?? null,
                isAdded,
            };
        }

        function initRowStates(items: FeedReconciliationRegSummary[]) {
            const states: Record<number, FeedMixRowState> = {};
            for (const item of items) {
                states[item.feedmix.id] = initRowState(item, false);
            }
            rowStates.value = states;
        }

        async function fetchSummary() {
            loading.value = true;
            errorMessage.value = null;
            try {
                let result: FeedReconciliationRegSummary[];
                if (props.reconciliationId !== null && props.reconciliationId !== undefined) {
                    const response: FeedReconciliationRegSummaryResponse = await store.dispatch(ActionType.GetFeedReconciliationRegSummaryById, {
                        id: props.reconciliationId,
                    });
                    result = response.feed_mixes;
                } else {
                    const form = props.value;
                    result = await store.dispatch(ActionType.GetFeedReconciliationRegSummary, {
                        locationId: form.location.locationId,
                        from: form.fromStatusDate,
                        to: form.toStatusDate,
                    });
                }
                summary.value = result ?? [];
                initRowStates(result ?? []);
                if (props.reconciliationId !== null && props.reconciliationId !== undefined && !hasAppliedActuals.value) {
                    hasAppliedActuals.value = true;
                    await applyExistingActuals();
                }
            } catch (error: any) {
                errorMessage.value = error?.data ?? i18n.global.t('global.errors.unknown');
            } finally {
                loading.value = false;
            }
        }

        async function addFeedMix(mix: Mix) {
            addSearch.value = '';
            showAddDropdown.value = false;
            errorMessage.value = null;

            const form = props.value;
            try {
                const result: FeedReconciliationRegSummary[] = await store.dispatch(ActionType.GetFeedReconciliationRegSummary, {
                    locationId: form.location.locationId,
                    from: form.fromStatusDate,
                    to: form.toStatusDate,
                    feedmixId: mix.id,
                });

                if (result && result.length > 0) {
                    const inTable = new Set((summary.value ?? []).map((s) => s.feedmix.id));
                    const newItems = result.filter((r) => !inTable.has(r.feedmix.id));
                    for (const item of newItems) {
                        rowStates.value[item.feedmix.id] = initRowState(item, true);
                    }
                    summary.value = [...newItems, ...(summary.value ?? [])];
                }
            } catch (error: any) {
                errorMessage.value = error?.data ?? i18n.global.t('global.errors.unknown');
            }
        }

        function onAddBlur() {
            // Small delay so mousedown on dropdown option fires before blur hides it
            setTimeout(() => {
                showAddDropdown.value = false;
            }, 150);
        }

        async function applyExistingActuals() {
            if (props.reconciliationId === null || props.reconciliationId === undefined) return;
            actualsLoading.value = true;
            try {
                const detail: FeedReconciliationDetail = await store.dispatch(ActionType.GetFeedReconciliationById, { id: props.reconciliationId });
                for (const mix of detail.feed_mixes) {
                    const state = rowStates.value[mix.feedmix_id];
                    if (!state) continue;
                    state.editMode = mix.edit_mode as 'aggregate' | 'components';
                    const summaryItem = summary.value?.find((i) => i.feedmix.id === mix.feedmix_id);
                    if (state.editMode === 'components' && summaryItem && !hasComponents(summaryItem)) {
                        state.editMode = 'aggregate';
                        state.componentsModeOverridden = true;
                    }
                    if (mix.actual_consumption !== null) state.actualConsumption = mix.actual_consumption;
                    if (mix.consumption_remainder !== null) state.remainder = mix.consumption_remainder;
                    if (mix.actual_price_per_100kg !== null) state.actualPrice = mix.actual_price_per_100kg!;
                    if (mix.actual_energy_content !== null) state.actualEnergyContent = mix.actual_energy_content;
                    for (const rm of mix.raw_materials) {
                        if (rm.actual_consumption !== null) {
                            state.rawActuals[rm.raw_material_id] = rm.actual_consumption;
                        }
                        if (rm.actual_price_per_100kg !== null && rm.actual_price_per_100kg !== undefined) {
                            state.rawPrices[rm.raw_material_id] = rm.actual_price_per_100kg;
                        }
                    }
                }
            } catch {
                errorMessage.value = i18n.global.t('global.errors.unknown');
            } finally {
                actualsLoading.value = false;
            }
        }

        async function onSubmitClicked() {
            if (!summary.value || isSubmitting.value) return;

            const payload = {
                location_id: props.value.location.locationId,
                from_status_date: props.value.fromStatusDate,
                to_status_date: props.value.toStatusDate,
                feed_mixes: summary.value.map((item) => {
                    const state = rowStates.value[item.feedmix.id];
                    const isCompound = isCompoundFeed(item);
                    return {
                        feedmix_id: item.feedmix.id,
                        edit_mode: state.editMode,
                        // Compound Feed: persist the derived consumption (so Feed Conversion uses it) and the
                        // entered remainder (so it seeds the next period's starting amount). Components mode
                        // derives consumption from raw materials, so actual_consumption stays null there.
                        actual_consumption: isCompound ? getCompoundConsumption(item) : (state.editMode === 'components' ? null : state.actualConsumption),
                        consumption_remainder: isCompound ? (state.remainder ?? 0) : null,
                        actual_price_per_100kg: state.actualPrice,
                        actual_energy_content: state.actualEnergyContent,
                        raw_materials: getEffectiveRawMaterials(item).map((m) => ({
                            raw_material_id: m.raw_material_id,
                            actual_consumption: state.rawActuals[m.raw_material_id] ?? null,
                            actual_price_per_100kg: state.rawPrices[m.raw_material_id] ?? null,
                        })),
                    };
                }),
            };

            isSubmitting.value = true;
            errorMessage.value = null;
            try {
                if (props.reconciliationId !== null && props.reconciliationId !== undefined) {
                    await store.dispatch(ActionType.UpdateFeedReconciliation, { form: { id: props.reconciliationId, update: payload } });
                } else {
                    await store.dispatch(ActionType.CreateFeedReconciliation, { form: payload });
                }
                queryClient.invalidateQueries({ queryKey: queryKeys.getFeedReconciliationsRoot });
                emit('submit');
            } catch (error: any) {
                if (error?.status === 409) {
                    errorMessage.value = i18n.global.t('views.feed.reconciliations.newModal.overlapWarning');
                } else {
                    errorMessage.value = error?.data ?? i18n.global.t('global.errors.unknown');
                }
            } finally {
                isSubmitting.value = false;
            }
        }

        onMounted(fetchSummary);

        return {
            loading,
            isSubmitting,
            errorMessage,
            actualsLoading,
            onSubmitClicked,
            summary,
            addSearch,
            showAddDropdown,
            availableMixes,
            filteredAvailableMixes,
            rowStates,
            formattedFromDate,
            formattedToDate,
            formatNumber,
            isType1Multi,
            isCompoundFeed,
            hasComponents,
            getEffectiveRawMaterials,
            canEditFeedMix,
            canEditRawMat,
            getFeedMixDisplayActual,
            getCompoundAvailable,
            getCompoundConsumption,
            onRemainderInput,
            totalRegisteredConsumption,
            totalActualConsumption,
            setEditMode,
            resetRow,
            removeRow,
            addFeedMix,
            onAddBlur,
            IconSource,
            priceUnitSuffix,
            energyUnitSuffix,
            toPositive,
            onNumberKeydown,
        };
    },
});
</script>
